import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

const base = (props: IconProps) => ({
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  ...props,
});

export const MicIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <rect x="9" y="2.5" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3.5M8.5 21.5h7" />
  </svg>
);

export const MicOffIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M15 5.5A3 3 0 0 0 9 5.5v4M9 12.2A3 3 0 0 0 15 12v-1" />
    <path d="M5 11a7 7 0 0 0 10.5 6M19 11v.6M12 18v3.5M8.5 21.5h7M3 3l18 18" />
  </svg>
);

export const CamIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <rect x="2.5" y="6" width="13" height="12" rx="3" />
    <path d="M15.5 10.5 21.5 7.5v9l-6-3z" />
  </svg>
);

export const CamOffIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M8 6h4.5a3 3 0 0 1 3 3v1.5M15.5 15.2a3 3 0 0 1-3 2.8H5.5a3 3 0 0 1-3-3V9a3 3 0 0 1 2.2-2.9" />
    <path d="M15.5 10.5 21.5 7.5v9l-3-1.5M3 3l18 18" />
  </svg>
);

export const NextIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M4 5.5 13 12l-9 6.5zM17.5 5v14" />
  </svg>
);

export const FlipIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M3.5 8.5A8.5 8.5 0 0 1 18 6.2M20.5 15.5A8.5 8.5 0 0 1 6 17.8" />
    <path d="M3.5 4v4.5H8M20.5 20v-4.5H16" />
  </svg>
);

export const ChatIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-5.1A8 8 0 1 1 21 12z" />
  </svg>
);

export const HeartIcon = ({ filled, ...props }: IconProps & { filled?: boolean }) => (
  <svg {...base(props)} fill={filled ? 'currentColor' : 'none'}>
    <path d="M12 20.3s-7.6-4.6-9.2-9.2A5.1 5.1 0 0 1 12 6.4a5.1 5.1 0 0 1 9.2 4.7c-1.6 4.6-9.2 9.2-9.2 9.2z" />
  </svg>
);

export const CloseIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export const FlagIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M5 21V4.2M5 4.2c4.5-2 7.5 2 12 0v9.6c-4.5 2-7.5-2-12 0z" />
  </svg>
);

export const SendIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M4 12 20.5 4 13.5 20.5 11.6 13z" />
  </svg>
);

export const SoundOnIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z" />
    <path d="M15.5 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" />
  </svg>
);

export const SoundOffIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z" />
    <path d="M16 9.5l5 5M21 9.5l-5 5" />
  </svg>
);

export const SparkleIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M12 2.5 14 9l6.5 2-6.5 2-2 6.5-2-6.5L3.5 11 10 9z" />
  </svg>
);

export const GlobeIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3.2 9.5h17.6M3.2 14.5h17.6M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z" />
  </svg>
);

export const ShieldIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M12 2.8 20 6v6c0 4.6-3.3 7.9-8 9.2-4.7-1.3-8-4.6-8-9.2V6z" />
    <path d="m8.8 12 2.2 2.2 4.2-4.4" />
  </svg>
);

export const BoltIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M13 2.5 4.5 13.5H11l-1 8L19.5 10H13z" />
  </svg>
);

export const PhoneOffIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M3 10.5c5.4-4.2 12.6-4.2 18 0v3.1l-4.2 1a1.6 1.6 0 0 1-1.9-1.1l-.5-1.8a11 11 0 0 0-4.8 0l-.5 1.8a1.6 1.6 0 0 1-1.9 1.1L3 13.6z" />
    <path d="M3 3l18 18" />
  </svg>
);

export const HourglassIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M6.5 2.5h11M6.5 21.5h11" />
    <path d="M7.5 2.5v3.2c0 2.4 4.5 4.4 4.5 6.3s-4.5 3.9-4.5 6.3v3.2M16.5 2.5v3.2c0 2.4-4.5 4.4-4.5 6.3s4.5 3.9 4.5 6.3v3.2" />
  </svg>
);

export const DiceIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <path d="M8.5 8.5h.01M15.5 8.5h.01M12 12h.01M8.5 15.5h.01M15.5 15.5h.01" strokeWidth="2.6" />
  </svg>
);

export const ReplayIcon = (props: IconProps) => (
  <svg {...base(props)}>
    <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
    <path d="M3 3.5V10h6.5" />
  </svg>
);
